import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import { useTranslation } from "react-i18next";
import { CheckCheck, Download, ExternalLink, FileArchive, ScanSearch, AlertTriangle } from "lucide-react";
import { receiptUrl, isImageMime, isPdf, isHeic } from "@/utils/receipt";

const now = new Date();

export default function ExpenseReview() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("submitted");
  const [employeeId, setEmployeeId] = useState("");
  const [category, setCategory] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [modal, setModal] = useState(null);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  const load = () => {
    const q = new URLSearchParams();
    if (status) q.append("status", status);
    if (employeeId) q.append("employee_id", employeeId);
    if (category) q.append("category", category);
    q.append("year", year); q.append("month", month);
    api.get(`/expenses?${q.toString()}`).then((r) => { setItems(r.data); setSelected(new Set()); });
  };
  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data)); }, []);
  useEffect(() => { api.get("/expense-categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, employeeId, category, year, month]);

  const empMap = useMemo(() => Object.fromEntries(emps.map((e) => [e.id, e])), [emps]);
  const submittedIds = useMemo(() => items.filter((e) => e.status === "submitted").map((e) => e.id), [items]);
  const allSelected = submittedIds.length > 0 && submittedIds.every((id) => selected.has(id));

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(submittedIds));

  const doReview = async (id, action, noteText = "") => {
    await api.post(`/expenses/${id}/review`, { action, note: noteText || null });
    setModal(null); setNote(""); setErr(""); load();
  };
  const openNoteModal = (row, action) => { setModal({ row, action }); setNote(""); setErr(""); };
  const submitNoteModal = () => {
    if (modal.action === "return" && !note.trim()) { setErr(t("common.requiredNote")); return; }
    doReview(modal.row.id, modal.action, note.trim());
  };

  // ---------- OCR check flow ----------
  const [checking, setChecking] = useState(false);
  const [approveAlert, setApproveAlert] = useState(null);  // { row }
  const runCheck = async () => {
    // Q4 default = only visible submitted rows on screen.
    const ids = items.filter((e) => e.status === "submitted").map((e) => e.id);
    if (ids.length === 0) { setToast(t("admin.noSelection")); setTimeout(() => setToast(""), 2000); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/expenses/check", { ids });
      // Merge results back into items so the UI refreshes without a full reload.
      setItems((prev) => prev.map((it) => {
        const upd = data.find((d) => d.id === it.id);
        return upd ? { ...it, ...upd } : it;
      }));
      setToast(t("common.zipDone", { count: data.length }));
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      setToast("Check failed: " + (e.message || ""));
      setTimeout(() => setToast(""), 2500);
    } finally { setChecking(false); }
  };

  // Approve with guard: if the row is flagged non-OK (or hasn't been checked), open alert.
  const guardedApprove = (row) => {
    const st = row.check_status;
    if (st === "ok") return doReview(row.id, "approve");
    // For any other status (mismatch / no_receipt / ocr_failed / manual_review / null)
    // — surface the alert modal so admin decides.
    setApproveAlert({ row });
  };
  const forceApprove = async () => {
    if (!approveAlert) return;
    await api.post(`/expenses/${approveAlert.row.id}/force-approve`);
    setApproveAlert(null); load();
  };

  const bulkApprove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { setToast(t("admin.noSelection")); setTimeout(() => setToast(""), 2000); return; }
    if (!window.confirm(t("admin.confirmBulkApprove", { count: ids.length }))) return;
    const { data } = await api.post("/expenses/bulk-approve", { ids });
    setToast(t("admin.bulkApproveDone", { count: data.approved_count }));
    setTimeout(() => setToast(""), 2500);
    load();
  };

  const [zipBusy, setZipBusy] = useState(false);
  const downloadZip = async () => {
    setZipBusy(true);
    try {
      const q = new URLSearchParams();
      if (status) q.append("status", status);
      if (employeeId) q.append("employee_id", employeeId);
      if (category) q.append("category", category);
      q.append("year", year); q.append("month", month);
      const res = await api.get(`/receipts/export/zip?${q.toString()}`, { responseType: "blob" });
      const count = Number(res.headers["x-receipt-count"] || 0);
      if (count === 0) {
        setToast(t("common.zipEmpty"));
        setTimeout(() => setToast(""), 2500);
        return;
      }
      // Extract filename from Content-Disposition if provided.
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : `receipts-${year}-${String(month).padStart(2, "0")}.zip`;
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast(t("common.zipDone", { count }));
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      setToast("ZIP: " + (e.message || "failed"));
      setTimeout(() => setToast(""), 2500);
    } finally { setZipBusy(false); }
  };

  // ---- Meal allowance weekly totals per driver (from all expenses in selected month) ----
  // Fetch approved+submitted meal items for the currently-selected month regardless of the status filter,
  // so the card is a stable weekly-policy view.
  const [mealWeekly, setMealWeekly] = useState([]);
  useEffect(() => {
    const q = new URLSearchParams({ category: "meal", year, month });
    api.get(`/expenses?${q.toString()}`).then((r) => {
      // group by employee_id + ISO week
      const map = new Map(); // key = empId||weekNum -> total
      const isoWeek = (dstr) => {
        const d = new Date(dstr);
        const target = new Date(d.valueOf());
        const dayNr = (d.getUTCDay() + 6) % 7;
        target.setUTCDate(target.getUTCDate() - dayNr + 3);
        const firstThursday = target.valueOf();
        target.setUTCMonth(0, 1);
        if (target.getUTCDay() !== 4) target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
        return 1 + Math.ceil((firstThursday - target) / 604800000);
      };
      for (const e of r.data) {
        if (e.status === "draft") continue;
        const wk = isoWeek(e.expense_date);
        const key = `${e.employee_id}||${wk}`;
        map.set(key, (map.get(key) || 0) + Number(e.amount || 0));
      }
      const rows = [];
      for (const [k, total] of map.entries()) {
        const [empId, wk] = k.split("||");
        rows.push({ employee_id: empId, week: Number(wk), total });
      }
      rows.sort((a, b) => a.week - b.week || (empMap[a.employee_id]?.name || "").localeCompare(empMap[b.employee_id]?.name || ""));
      setMealWeekly(rows);
    }).catch(() => setMealWeekly([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, emps.length]);

  const mealClass = (total) => {
    if (total > 9000) return "meal-over";
    if (total >= 6000) return "meal-ok";
    return "meal-under";
  };
  const mealLabel = (total) => {
    if (total > 9000) return t("admin.mealOver");
    if (total >= 6000) return t("admin.mealOk");
    return t("admin.mealUnder");
  };

  return (
    <div>
      <h1 className="page-title">{t("admin.expenses")}</h1>

      {mealWeekly.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }} data-testid="meal-weekly-card">
          <div className="card-body">
            <div className="card-title">{t("admin.mealWeeklyTitle")}</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>{t("admin.mealWeeklyHint")}</div>
            <div className="meal-weekly-grid">
              {mealWeekly.map((row, i) => (
                <div key={i} className={`meal-chip ${mealClass(row.total)}`} data-testid={`meal-chip-${row.employee_id}-${row.week}`}>
                  <div style={{ fontSize: 11, opacity: 0.8 }}>{t("common.week")} {row.week}</div>
                  <div style={{ fontWeight: 700 }}>{empMap[row.employee_id]?.name || "-"}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>¥{Number(row.total).toLocaleString()}</div>
                  <div style={{ fontSize: 11, marginTop: 2 }}>{mealLabel(row.total)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="row-actions" style={{ marginBottom: 12, alignItems: "center" }}>
        <select className="select" style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="exp-filter-status">
          <option value="">{t("common.all")}</option>
          <option value="submitted">{t("common.submitted")}</option>
          <option value="approved">{t("common.approved")}</option>
          <option value="rejected">{t("common.rejected")}</option>
          <option value="returned">{t("common.returned")}</option>
        </select>
        <select className="select" style={{ width: 200 }} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
          <option value="">{t("admin.filterEmployee")}</option>
          {emps.map((e) => <option key={e.id} value={e.id}>{e.employee_code} - {e.name}</option>)}
        </select>
        <select className="select" style={{ width: 160 }} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">{t("admin.filterCategory")}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.code}>{i18n.language === "en" ? c.label_en : c.label_ja}</option>
          ))}
        </select>
        <input className="input" type="number" style={{ width: 100 }} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="exp-filter-year" />
        <select className="select" style={{ width: 110 }} value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="exp-filter-month">
          {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{i+1}月</option>)}
        </select>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "#64748B" }}>選択: {selected.size} / {submittedIds.length}</span>
          <button className="btn btn-outline" onClick={runCheck} disabled={checking || submittedIds.length === 0}
                  data-testid="exp-check-btn" title={t("common.checkExpenses")}>
            <ScanSearch size={14}/> {checking ? t("common.checking") : t("common.checkExpenses")}
          </button>
          <button className="btn btn-outline" onClick={downloadZip} disabled={zipBusy} data-testid="exp-zip-download" title={t("common.downloadZip")}>
            <FileArchive size={14}/> {zipBusy ? t("common.zipPreparing") : t("common.downloadZip")}
          </button>
          <button className="btn btn-success" onClick={bulkApprove} disabled={selected.size === 0} data-testid="exp-bulk-approve">
            <CheckCheck size={14}/> {t("admin.approveSelected")}
          </button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
        <div className="card"><div className="card-body table-wrap">
          <table className="table" data-testid="exp-review-table">
            <thead><tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="exp-select-all" disabled={submittedIds.length === 0} />
              </th>
              <th>{t("common.date")}</th><th>{t("admin.name")}</th><th>{t("driver.category")}</th><th>{t("driver.description")}</th><th>{t("driver.amount")}</th><th>領収書</th><th>{t("common.checkColumn")}</th><th>{t("common.status")}</th><th>{t("common.actions")}</th>
            </tr></thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} style={selected.has(e.id) ? { background: "#EEF4FF" } : {}}>
                  <td>
                    {e.status === "submitted" && (
                      <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} data-testid={`exp-check-${e.id}`} />
                    )}
                  </td>
                  <td>{e.expense_date}</td><td>{empMap[e.employee_id]?.name || "-"}</td>
                  <td>{(categories.find((c) => c.code === e.category)) ? (i18n.language === "en" ? categories.find((c) => c.code === e.category).label_en : categories.find((c) => c.code === e.category).label_ja) : e.category}</td><td>{e.description}</td>
                  <td>¥{Number(e.amount).toLocaleString()}</td>
                  <td>
                    {(e.receipts || []).map((r) => (
                      <button key={r.id} className="receipt-thumb" onClick={() => setPreview(r)}>{r.original_name}</button>
                    ))}
                  </td>
                  <td data-testid={`exp-check-status-${e.id}`}>
                    <CheckBadge row={e} t={t} />
                  </td>
                  <td>
                    <span className={`tag tag-${e.status}`}>{t(`common.${e.status}`)}</span>
                    {e.review_note && (e.status === "rejected" || e.status === "returned") && (
                      <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{e.review_note}</div>
                    )}
                  </td>
                  <td>
                    {e.status === "submitted" ? (
                      <div className="row-actions">
                        <button className="btn btn-success" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => guardedApprove(e)} data-testid={`exp-approve-${e.id}`}>{t("common.approve")}</button>
                        <button className="btn btn-danger" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => openNoteModal(e, "reject")} data-testid={`exp-reject-${e.id}`}>{t("common.reject")}</button>
                        <button className="btn btn-warning" style={{ padding: "6px 10px", fontSize: 12 }} onClick={() => openNoteModal(e, "return")} data-testid={`exp-return-${e.id}`}>{t("common.return")}</button>
                      </div>
                    ) : <span style={{ color: "#64748B", fontSize: 12 }}>—</span>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="10" style={{ textAlign: "center", color: "#64748B" }}>{t("driver.noRecords")}</td></tr>}
            </tbody>
          </table>
        </div></div>
        {preview && (
          <div className="modal-backdrop" data-testid="receipt-preview-modal" onClick={() => setPreview(null)}>
            <div className="modal-card" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 720, maxHeight: "90vh", overflow: "auto" }}>
              <div className="modal-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t("common.receipt")}: {preview.original_name}</span>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  <a className="btn btn-outline" style={{ padding: "4px 10px", fontSize: 12 }}
                     href={receiptUrl(preview.file_url)} target="_blank" rel="noreferrer"
                     data-testid="receipt-preview-open-tab">
                    <ExternalLink size={12} /> {t("common.openInNewTab")}
                  </a>
                  <a className="btn btn-primary" style={{ padding: "4px 10px", fontSize: 12 }}
                     href={receiptUrl(preview.file_url)} download={preview.original_name || "receipt"}
                     data-testid="receipt-preview-download">
                    <Download size={12} /> {t("common.download")}
                  </a>
                  <button className="btn btn-outline" style={{ padding: "4px 10px" }} onClick={() => setPreview(null)} data-testid="receipt-preview-close">×</button>
                </div>
              </div>
              {isImageMime(preview.mime_type, preview.original_name) ? (
                <img alt="receipt" src={receiptUrl(preview.file_url)} style={{ width: "100%", borderRadius: 10, display: "block" }} data-testid="receipt-preview-img" />
              ) : isPdf(preview.mime_type, preview.original_name) ? (
                <iframe title="receipt" src={receiptUrl(preview.file_url)} style={{ width: "100%", height: 600, border: 0 }} data-testid="receipt-preview-iframe" />
              ) : (
                <div style={{ padding: 24, textAlign: "center", color: "#64748B" }} data-testid="receipt-preview-fallback">
                  {isHeic(preview.mime_type, preview.original_name)
                    ? "HEIC / HEIF 画像はブラウザで直接表示できません。ダウンロードしてご確認ください。"
                    : t("common.noPreview")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-backdrop" data-testid="exp-note-modal" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-title">
              {modal.action === "return" ? t("common.return") : t("common.reject")} — {modal.row.expense_date} / {empMap[modal.row.employee_id]?.name}
            </div>
            <div className="field">
              <label className="label">{t("common.note")} {modal.action === "return" && <span style={{ color: "#DC2626" }}>*</span>}</label>
              <textarea data-testid="exp-note-input" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder={modal.action === "return" ? t("common.requiredNote") : ""} />
            </div>
            {err && <div style={{ color: "#DC2626", marginBottom: 8 }} data-testid="exp-note-err">{err}</div>}
            <div className="row-actions">
              <button className="btn btn-outline" onClick={() => setModal(null)}>{t("common.cancel")}</button>
              <button className={modal.action === "return" ? "btn btn-warning" : "btn btn-danger"} onClick={submitNoteModal} data-testid="exp-note-submit">
                {modal.action === "return" ? t("common.return") : t("common.reject")}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast" data-testid="exp-toast">{toast}</div>}

      {approveAlert && (
        <div className="modal-backdrop" data-testid="approve-alert-modal" onClick={() => setApproveAlert(null)}>
          <div className="modal-card" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-title" style={{ display: "flex", alignItems: "center", gap: 8, color: "#B45309" }}>
              <AlertTriangle size={18}/> {t("common.approveAlertTitle")}
            </div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 14, marginBottom: 12 }} data-testid="approve-alert-body">
              {t("common.approveAlertBody", {
                date: approveAlert.row.expense_date,
                note: approveAlert.row.check_note || t("common.ocrNotChecked"),
              })}
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
              <div>{t("driver.amount")}: <b>¥{Number(approveAlert.row.amount).toLocaleString()}</b></div>
              {approveAlert.row.check_amount != null && (
                <div>OCR: <b>¥{Number(approveAlert.row.check_amount).toLocaleString()}</b></div>
              )}
            </div>
            <div className="row-actions">
              <button className="btn btn-outline" onClick={() => setApproveAlert(null)} data-testid="approve-alert-cancel">
                {t("common.cancelApprove")}
              </button>
              <button className="btn btn-danger" onClick={forceApprove} data-testid="approve-alert-force">
                {t("common.forceApprove")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Small badge for the OCR check column --------
function CheckBadge({ row, t }) {
  const s = row.check_status;
  const map = {
    ok:            { cls: "tag tag-approved",  emoji: "✅", key: "common.ocrOk" },
    mismatch:      { cls: "tag tag-rejected",  emoji: "❌", key: "common.ocrMismatch" },
    no_receipt:    { cls: "tag tag-returned",  emoji: "⚠", key: "common.ocrNoReceipt" },
    ocr_failed:    { cls: "tag tag-returned",  emoji: "⚠", key: "common.ocrFailed" },
    manual_review: { cls: "tag tag-submitted", emoji: "⚠", key: "common.ocrManualReview" },
  };
  if (!s) return <span className="tag tag-draft">{t("common.ocrNotChecked")}</span>;
  const cfg = map[s] || map.ocr_failed;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span className={cfg.cls}>{cfg.emoji} {t(cfg.key)}</span>
      {row.check_note && (
        <span style={{ fontSize: 10, color: "#64748B", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.check_note}>
          {row.check_note}
        </span>
      )}
    </div>
  );
}
